import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useMode } from '../contexts/ModeContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getToken } from '../utils/auth';
import BannerAd from '../components/BannerAd';
import { apiBaseUrl } from '../config/runtime';
import {
  Search, Plus, MessageCircle, ClipboardList,
  Wrench, TrendingUp, CheckCircle, Wallet,
  Calendar, PiggyBank, RefreshCw, ChevronRight,
  LogOut, Layers, AlertCircle
} from 'lucide-react';

/* ── 스타일 상수 ── */
const INDIGO = '#a78bfa';
const card: React.CSSProperties = {
  background: '#0d0d0d',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 16,
  padding: 24,
};

/* ── StatCard ── */
const StatCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  accent?: string;
}> = ({ icon, label, value, accent = INDIGO }) => (
  <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 12 }}>
    <div style={{
      width: 40, height: 40, borderRadius: 10,
      background: accent + '15',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: accent,
    }}>
      {icon}
    </div>
    <div>
      <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: '#fff', letterSpacing: '-0.5px' }}>{value}</div>
    </div>
  </div>
);

/* ── ActionCard ── */
const ActionCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  desc: string;
  onClick: () => void;
}> = ({ icon, title, desc, onClick }) => (
  <div
    onClick={onClick}
    style={{
      display: 'flex', alignItems: 'center', gap: 14,
      padding: '14px 16px', borderRadius: 12,
      border: '1px solid rgba(255,255,255,0.08)', cursor: 'pointer',
      transition: 'all 0.15s', background: 'rgba(255,255,255,0.03)',
    }}
    onMouseOver={e => { e.currentTarget.style.background = 'rgba(167,139,250,0.08)'; e.currentTarget.style.borderColor = 'rgba(167,139,250,0.25)'; }}
    onMouseOut={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
  >
    <div style={{
      width: 36, height: 36, borderRadius: 9,
      background: INDIGO + '12', color: INDIGO,
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      {icon}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>{title}</div>
      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{desc}</div>
    </div>
    <ChevronRight size={16} color="rgba(255,255,255,0.2)" />
  </div>
);

/* ── StepBox ── */
const StepBox: React.FC<{ n: number; title: string; desc: string; last?: boolean }> = ({ n, title, desc, last }) => (
  <div style={{ display: 'flex', gap: 12, position: 'relative' }}>
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{
        width: 28, height: 28, borderRadius: 8, background: INDIGO,
        color: '#fff', fontSize: 12, fontWeight: 700,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>{n}</div>
      {!last && <div style={{ width: 1, flex: 1, background: 'rgba(255,255,255,0.08)', marginTop: 4, minHeight: 16 }} />}
    </div>
    <div style={{ paddingBottom: last ? 0 : 18 }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>{title}</div>
      <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>{desc}</div>
    </div>
  </div>
);

/* ── Main ── */
const DashboardPage: React.FC = () => {
  const { user, logout } = useAuth();
  const { currentMode, isClientMode } = useMode();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [stats, setStats] = useState({
    pendingMessages: 0, savedAmount: 0,
    totalProjects: 0, activeProjects: 0,
    completedProjects: 0, inProgressTrades: 0,
    completedTrades: 0, totalSpent: 0,
    totalServices: 0, activeServices: 0,
    totalEarnings: 0, monthlyEarnings: 0, averageRating: '0.0'
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const token = getToken();
      if (!token) throw new Error(t('dashboard.noToken'));
      const endpoint = isClientMode ? apiBaseUrl('/dashboard/client') : apiBaseUrl('/dashboard/freelancer');
      console.log(`대시보드 API 호출: ${endpoint}, 모드: ${currentMode}`);
      const res = await fetch(endpoint, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error((await res.json()).message || `HTTP ${res.status}`);
      const data = await res.json();
      console.log('대시보드 통계 수신:', data);
      setStats(prev => ({ ...prev, ...data }));
    } catch (err) {
      console.error('대시보드 통계 조회 실패:', err);
      setError(err instanceof Error ? err.message : t('dashboard.statsFailed'));
    } finally { setLoading(false); }
  }, [isClientMode, currentMode, t]);

  const fetchUnread = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await fetch(apiBaseUrl('/dashboard/unread-count'), { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setStats(prev => ({ ...prev, pendingMessages: data.unreadCount || 0 }));
      }
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    console.log('대시보드 페이지 로드됨, 사용자:', user);
    if (user) {
      fetchStats();
      const t = setInterval(fetchUnread, 30000);
      return () => clearInterval(t);
    }
  }, [user, fetchStats, fetchUnread]);

  const clientActions = [
    { icon: <Search size={16} />, title: t('home.findExperts'), desc: t('dashboard.findExpertsDesc'), onClick: () => navigate('/services') },
    { icon: <Plus size={16} />, title: t('home.postProject'), desc: t('dashboard.postProjectDesc'), onClick: () => navigate('/project-create') },
    { icon: <MessageCircle size={16} />, title: t('nav.chat'), desc: t('dashboard.chatClientDesc'), onClick: () => navigate('/chat') },
    { icon: <ClipboardList size={16} />, title: t('transactions.historyTitle'), desc: t('dashboard.transactionsDesc'), onClick: () => navigate('/transactions') },
  ];
  const freelancerActions = [
    { icon: <Search size={16} />, title: t('dashboard.findProjects'), desc: t('dashboard.findProjectsDesc'), onClick: () => navigate('/projects') },
    { icon: <Plus size={16} />, title: t('dashboard.createService'), desc: t('dashboard.createServiceDesc'), onClick: () => navigate('/service-create') },
    { icon: <MessageCircle size={16} />, title: t('nav.chat'), desc: t('dashboard.chatFreelancerDesc'), onClick: () => navigate('/chat') },
    { icon: <ClipboardList size={16} />, title: t('transactions.historyTitle'), desc: t('dashboard.transactionsDesc'), onClick: () => navigate('/transactions') },
  ];
  const actions = isClientMode ? clientActions : freelancerActions;

  if (!user) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', color: '#999' }}>
      {t('common.loginRequired')}
    </div>
  );

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 20px', background: '#000', minHeight: '100vh' }}>

      {/* 광고 배너 */}
      <div style={{ marginBottom: 20 }}><BannerAd position="top" /></div>

      {/* 상단 헤더 */}
      <div style={{
        background: `linear-gradient(135deg, rgba(124,58,237,0.35) 0%, rgba(219,39,119,0.25) 100%)`,
        borderRadius: 16, padding: '28px 32px', marginBottom: 28, color: '#fff',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16,
        border: '1px solid rgba(124,58,237,0.2)',
      }}>
        <div>
          <div style={{ fontSize: 13, opacity: 0.55, marginBottom: 4 }}>
            {isClientMode ? t('dashboard.clientMode') : t('dashboard.freelancerMode')} · {user.email}
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 800, margin: 0, letterSpacing: '-0.5px' }}>
            {t('dashboard.greeting', { name: user.name || user.nickname || t('dashboard.user') })}
          </h1>
          {loading && <div style={{ fontSize: 13, opacity: 0.7, marginTop: 6 }}>{t('dashboard.loadingStats')}</div>}
          {error && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#FCA5A5', marginTop: 6 }}>
              <AlertCircle size={14} />{error}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={fetchStats}
            disabled={loading}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'rgba(255,255,255,0.1)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)',
              padding: '9px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500,
              cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1,
            }}
          >
            <RefreshCw size={14} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
            {t('dashboard.refresh')}
          </button>
          <button
            onClick={() => { if (window.confirm(t('dashboard.confirmLogout'))) { logout(); navigate('/'); } }}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'rgba(255,255,255,0.1)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)',
              padding: '9px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer',
            }}
          >
            <LogOut size={14} />{t('nav.logout')}
          </button>
        </div>
      </div>

      {/* 통계 카드 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 16, marginBottom: 28 }}>
        {isClientMode ? (<>
          <StatCard icon={<Layers size={18} />} label={t('dashboard.totalProjects')} value={t('dashboard.countN', { n: stats.totalProjects })} />
          <StatCard icon={<TrendingUp size={18} />} label={t('dashboard.inProgressTrades')} value={t('dashboard.countN', { n: stats.inProgressTrades })} accent="#10B981" />
          <StatCard icon={<CheckCircle size={18} />} label={t('dashboard.completedTrades')} value={t('dashboard.countN', { n: stats.completedTrades })} accent="#0EA5E9" />
          <StatCard icon={<Wallet size={18} />} label={t('dashboard.totalSpent')} value={`₩${(stats.totalSpent || 0).toLocaleString()}`} accent="#F59E0B" />
          <StatCard icon={<MessageCircle size={18} />} label={t('dashboard.pendingMessages')} value={t('dashboard.countN', { n: stats.pendingMessages })} accent="#EF4444" />
          <StatCard icon={<PiggyBank size={18} />} label={t('dashboard.savedAmount')} value={`₩${(stats.savedAmount || 0).toLocaleString()}`} accent="#8B5CF6" />
        </>) : (<>
          <StatCard icon={<Wrench size={18} />} label={t('dashboard.activeServices')} value={t('dashboard.countN', { n: stats.activeServices })} />
          <StatCard icon={<TrendingUp size={18} />} label={t('dashboard.inProgressTrades')} value={t('dashboard.countN', { n: stats.inProgressTrades })} accent="#10B981" />
          <StatCard icon={<CheckCircle size={18} />} label={t('dashboard.completedTrades')} value={t('dashboard.countN', { n: stats.completedTrades })} accent="#0EA5E9" />
          <StatCard icon={<Wallet size={18} />} label={t('dashboard.totalEarnings')} value={`₩${(stats.totalEarnings || 0).toLocaleString()}`} accent="#F59E0B" />
          <StatCard icon={<Calendar size={18} />} label={t('dashboard.monthlyEarnings')} value={`₩${(stats.monthlyEarnings || 0).toLocaleString()}`} accent="#8B5CF6" />
          <StatCard icon={<PiggyBank size={18} />} label={t('dashboard.savedAmount')} value={`₩${(stats.savedAmount || 0).toLocaleString()}`} accent="#EC4899" />
        </>)}
      </div>

      {/* 하단 2단 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

        {/* 빠른 작업 */}
        <div style={card}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: '#fff', marginBottom: 16, letterSpacing: '-0.3px' }}>
            {t('dashboard.quickActions')}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {actions.map((a, i) => (
              <ActionCard key={i} icon={a.icon} title={a.title} desc={a.desc} onClick={a.onClick} />
            ))}
          </div>
        </div>

        {/* 이용 안내 */}
        <div style={card}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: '#fff', marginBottom: 16, letterSpacing: '-0.3px' }}>
            {t('serviceDetail.howTo')}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <StepBox n={1} title={t('dashboard.step1')} desc={t('dashboard.step1Desc')} />
            <StepBox n={2} title={t('dashboard.step2')} desc={t('dashboard.step2Desc')} />
            <StepBox n={3} title={t('dashboard.step3')} desc={t('dashboard.step3Desc')} />
            <StepBox n={4} title={t('dashboard.step4')} desc={t('dashboard.step4Desc')} last />
          </div>
          <div style={{
            marginTop: 20, padding: '12px 16px',
            background: 'rgba(16,185,129,0.08)',
            border: '1px solid rgba(16,185,129,0.2)',
            borderRadius: 10,
            fontSize: 13, color: '#34d399',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <CheckCircle size={14} />
            <span><strong>{t('dashboard.fee0')}</strong> — {t('dashboard.fee0Desc')}</span>
          </div>
        </div>

      </div>

      {/* 하단 여백 */}
      <div style={{ marginTop: 40, textAlign: 'center', color: '#CCC', fontSize: 13 }}>
        {t('dashboard.footer')}
      </div>
    </div>
  );
};

export default DashboardPage;
